import Link from 'next/link';
import emojiFlags from 'emoji-flags';
import { nameFromCC } from 'tf-post-parser';
import itineraryMapboxUrl from 'helpers/itineraryMapboxUrl';
import { fraunces } from '../Landing/fonts';
import { MUTED, PAPER_CARD } from '../Landing/ui';
import FutureImage from '../FutureImage';
import MapboxCredits from '../Maps/MapboxCredits';

function flagSrc(countryCode) {
  if (!countryCode || !emojiFlags.countryCode(countryCode.toUpperCase()))
    return null;
  return `/flags/${countryCode.toLowerCase()}.svg`;
}

function formatDuration(days) {
  if (days % 7 === 0) {
    const weeks = days / 7;
    return `${weeks} week${weeks === 1 ? '' : 's'}`;
  }
  return `${days} day${days === 1 ? '' : 's'}`;
}

function ItineraryCard({ itinerary }) {
  const flag = flagSrc(itinerary.countryCode);
  const countryName = nameFromCC(itinerary.countryCode);
  const places = itinerary.places || [];
  // 640x400 matches the 16/10 frame exactly — no crop, route pins stay visible.
  const mapUrl = itineraryMapboxUrl(
    itinerary,
    { countryCode: itinerary.countryCode },
    false,
    640,
  );

  return (
    <article
      className={`${PAPER_CARD} tw-group tw-relative tw-overflow-hidden tw-rounded-2xl tw-shadow-[0_14px_32px_-18px_rgba(29,45,40,0.38)] tw-transition-all tw-duration-300 hover:tw-shadow-[0_28px_52px_-18px_rgba(29,45,40,0.55)] hover:-tw-translate-y-1.5`}
    >
      {/* Stretched link: covers the card without nesting the credits' anchors */}
      <Link
        href={`/@${itinerary.author.username}/itineraries/${itinerary.permlink}`}
        prefetch={false}
        aria-label={itinerary.title}
        className="tw-absolute tw-inset-0 tw-z-10"
      />

      <div className="tw-relative tw-aspect-[16/10] tw-overflow-hidden">
        {mapUrl ? (
          <>
            <FutureImage
              src={mapUrl}
              alt={`${itinerary.title} route map`}
              fill
              sizes="(max-width: 992px) 100vw, 480px"
              className="tw-object-cover"
            />

            {/* Legibility gradient for the overlaid title */}
            <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-[rgba(10,22,18,0.72)] tw-via-[rgba(10,22,18,0.08)] tw-to-transparent" />
          </>
        ) : (
          // No map for an itinerary without a country or without a single
          // located place. The frame carries the title, so it stays and the
          // map is replaced by the journal plate rather than an empty box.
          <div className="tw-absolute tw-inset-0 tw-bg-[#FFFDF6] dark:tw-bg-[#15231E]">
            <svg
              aria-hidden="true"
              className="tw-h-full tw-w-full tw-text-[#1D2D28] tw-opacity-[0.07] dark:tw-text-[#EBE6D9] dark:tw-opacity-[0.09]"
              preserveAspectRatio="xMidYMid slice"
              viewBox="0 0 800 400"
            >
              <g fill="none" stroke="currentColor" strokeWidth="1.2">
                <path d="M-40 60c120-45 240 35 360-5s215-60 360-15 160 55 160 55" />
                <path d="M-40 105c120-45 240 35 360-5s215-60 360-15 160 55 160 55" />
                <path d="M-40 330c135-60 255 45 375 0s200-75 345-20 160 60 160 60" />
              </g>
            </svg>
          </div>
        )}

        {flag && (
          <div className="tw-absolute tw-left-3 tw-top-3 tw-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-white/90 tw-px-2.5 tw-py-1 tw-shadow-md tw-backdrop-blur-sm">
            <img
              src={flag}
              alt=""
              aria-hidden="true"
              width={14}
              height={14}
              className="tw-h-[14px] tw-w-[14px]"
            />
            <span className="tw-text-[11px] tw-font-semibold tw-tracking-wide tw-text-[#1D2D28]">
              {countryName}
            </span>
          </div>
        )}

        <div className="tw-absolute tw-right-3 tw-top-3 tw-rounded-full tw-bg-[rgba(10,22,18,0.65)] tw-px-2.5 tw-py-1 tw-backdrop-blur-sm">
          <span className="tw-font-mono tw-text-[11px] tw-font-semibold tw-tracking-widest tw-text-white/90">
            {formatDuration(itinerary.duration)}
          </span>
        </div>

        <div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-p-4">
          <h3
            className={`${fraunces.className} tw-m-0 tw-text-xl tw-font-semibold tw-leading-snug tw-tracking-tight ${
              mapUrl
                ? 'tw-text-white'
                : 'tw-text-[#1D2D28] dark:tw-text-[#EBE6D9]'
            }`}
          >
            {itinerary.title}
          </h3>
        </div>

        {/* Above the stretched link so the attribution stays clickable */}
        {mapUrl && (
          <div className="tw-absolute tw-bottom-0 tw-right-0 tw-z-20 tw-opacity-80">
            <MapboxCredits />
          </div>
        )}
      </div>

      {/* Waypoints as a flight route: numbered stops, dashed legs */}
      {places.length > 0 && (
        <div className="tw-overflow-x-auto tw-border-0 tw-border-t tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-px-4 tw-py-3 [scrollbar-width:none]">
          <div className="tw-flex tw-items-center tw-whitespace-nowrap">
            {places.slice(0, 6).map((place, i) => (
              <div key={place._id || i} className="tw-flex tw-items-center">
                <div className="tw-flex tw-items-center tw-gap-1.5">
                  <span className="tw-flex tw-h-[18px] tw-w-[18px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary-600/10 dark:tw-bg-primary-300/20 tw-font-mono tw-text-[9px] tw-font-bold tw-text-primary-700 dark:tw-text-primary-300">
                    {i + 1}
                  </span>
                  <span className={`${MUTED} tw-text-[12px] tw-font-medium`}>
                    {place.name}
                  </span>
                </div>
                {i < Math.min(places.length, 6) - 1 && (
                  <span
                    aria-hidden="true"
                    className="tw-mx-2.5 tw-w-5 tw-border-0 tw-border-t-2 tw-border-dashed tw-border-[#C9C0AA] dark:tw-border-[#3A4D44]"
                  />
                )}
              </div>
            ))}
            {places.length > 6 && (
              <span className={`${MUTED} tw-ml-2.5 tw-font-mono tw-text-[11px]`}>
                +{places.length - 6} stops
              </span>
            )}
          </div>
        </div>
      )}
    </article>
  );
}

export default function ItinerariesList({ initialItineraries }) {
  if (!initialItineraries || initialItineraries.length === 0) return null;

  return (
    <div className="tw-grid tw-grid-cols-1 tw-gap-6 lg:tw-grid-cols-2">
      {initialItineraries.map(itinerary => (
        <ItineraryCard
          key={`${itinerary.author.username}-${itinerary.permlink}`}
          itinerary={itinerary}
        />
      ))}
    </div>
  );
}
